import { useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type { MarketingTemplate } from '@/lib/types';
import { cx } from '@/lib/cx';
import { Button, Combobox, type ComboboxOption, Field, Modal } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * The three ways to start a template: a blank visual one, imported HTML, or a
 * copy of an existing template. The blank one is not created here — the editor
 * page opens on `/marketing/templates/new` and creates on its first Save, so an
 * abandoned "New" never leaves an empty row behind.
 */

export interface NewTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onBlank: () => void;
  onImport: () => void;
  onDuplicated: (template: MarketingTemplate) => void;
}

type Choice = 'blank' | 'import' | 'duplicate';

const CHOICES: readonly { value: Choice; title: string; detail: string }[] = [
  { value: 'blank', title: 'Blank visual template', detail: 'Drag-and-drop builder' },
  { value: 'import', title: 'Import HTML', detail: 'Paste or upload an .html file' },
  { value: 'duplicate', title: 'Duplicate a template', detail: 'Start from an existing one' },
];

export function NewTemplateModal({ open, onClose, onBlank, onImport, onDuplicated }: NewTemplateModalProps) {
  const [choice, setChoice] = useState<Choice>('blank');
  const [sourceId, setSourceId] = useState('');
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setChoice('blank');
    setSourceId('');
    setSearch('');
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open || choice !== 'duplicate') return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setOptionsLoading(true);
      api
        .listMarketingTemplates(
          { page: 1, perPage: 50, ...(search.trim() ? { search: search.trim() } : {}) },
          controller.signal,
        )
        .then((page) => {
          setOptions(
            page.data.map((template) => ({
              value: String(template.id),
              label: template.name,
              ...(template.subject ? { hint: template.subject } : {}),
            })),
          );
          setHasMore(page.meta.total > page.data.length);
          setOptionsError(null);
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted) return;
          setOptions([]);
          setOptionsError(caught instanceof Error ? caught.message : 'Templates could not be loaded.');
        })
        .finally(() => {
          if (!controller.signal.aborted) setOptionsLoading(false);
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, choice, search]);

  async function proceed() {
    setError(null);
    if (choice === 'blank') {
      onBlank();
      return;
    }
    if (choice === 'import') {
      onImport();
      return;
    }
    if (!sourceId) {
      setError('Choose a template to duplicate.');
      return;
    }
    setWorking(true);
    try {
      const response = await api.duplicateMarketingTemplate(Number(sourceId));
      onDuplicated(response.data);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? (caught.formMessage ?? caught.message)
          : 'Could not reach the server. Nothing was duplicated.',
      );
    } finally {
      setWorking(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New template"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={working}>
            Cancel
          </Button>
          <Button variant="primary" loading={working} onClick={() => void proceed()}>
            {choice === 'duplicate' ? 'Duplicate' : 'Continue'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <div role="radiogroup" aria-label="How to start" className="grid gap-2">
          {CHOICES.map((item) => {
            const selected = choice === item.value;
            return (
              <button
                key={item.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setChoice(item.value)}
                className={cx(
                  'flex w-full flex-col items-start rounded-md border px-3 py-2.5 text-left transition-colors duration-150 ease-out motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-focus-ring',
                  selected
                    ? 'border-fg bg-surface-active'
                    : 'border-border bg-surface hover:bg-surface-hover',
                )}
              >
                <span className="text-sm font-medium text-fg">{item.title}</span>
                <span className="text-xs text-fg-muted">{item.detail}</span>
              </button>
            );
          })}
        </div>

        {choice === 'duplicate' && (
          <Field label="Template to duplicate" error={optionsError ?? undefined}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                value={sourceId}
                onChange={setSourceId}
                options={options}
                placeholder="Choose a template…"
                searchPlaceholder="Search templates…"
                onSearch={setSearch}
                loading={optionsLoading}
                invalid={Boolean(optionsError)}
                {...(hasMore ? { footnote: 'Showing the first 50 matches — search to narrow.' } : {})}
                aria-describedby={describedBy}
              />
            )}
          </Field>
        )}

        {error && (
          <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
            {error}
          </Notice>
        )}
      </div>
    </Modal>
  );
}
